import * as THREE from 'three';
import { basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Ser Corvin, the Knight of the Gate (docs/ANTIHEROES.md): the first of the Unsleeping, a guardian of the old
 * garrison who held the gate with his shoulder until it gave. The titan dreams him in plate turned to black glass,
 * the Burn's glass, cracked through with embers; a closed helm crowned with the broken iron of the gate's
 * portcullis, two embers for eyes; on his left arm a slab of the gate itself — charred planks bound in iron,
 * glowing through the seams — and in his right a long sword of black glass with a burning edge. A ragged mantle
 * of smoke hangs from his shoulders and breathes off him as any nightmare's wings do.
 *
 * Built on the fallen sentinel's bones (anim/creatures/corvin.ts reuses its walk, reel, stagger and fall): body
 * (hips), chest, head, the tabard's front and back flaps, armL/foreL/shield (the gate slab), armR/foreR/sword,
 * legs (thigh, shin). Faces +Z, `R` limbs on +X. Modelled in his guard; no rest rotations.
 */

/** Model height (the crown's tallest spike). */
export const CORVIN_HEIGHT = 2.3;

export const corvin = (o?: BaseOpts): Model => {
  const c = begin(o, 0.66, 733);
  const { g, rng } = c;
  const k4 = kit(rng);

  // Black glass, glossy enough to catch the key light from the table camera; its duller joints; ember light.
  const glass = metal('#2c303b', { roughness: 0.16, metalness: 0.5, bumpScale: 0.01 });
  const glassD = metal('#1a1c23', { roughness: 0.28, metalness: 0.5, bumpScale: 0.02 });
  const mail = std('#16171c', { roughness: 0.65, metalness: 0.35, bumpScale: 0.06 });
  const rag = cloth('#241517', { side: THREE.DoubleSide });
  const iron = metal('#3a3d44', { roughness: 0.55, metalness: 0.6, bumpScale: 0.04 });
  const char = std('#1c1410', { roughness: 0.9, bumpScale: 0.08 });
  const ember = emis('#ffd9b0', '#ff4f3a', 2.8);
  const emberF = emis('#ffc59a', '#ff6a3a', 2.0, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#14151b', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: glass cuisses and greaves, knee cops, sabatons; embers in the joints ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.16, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.16, 1.02, 0), V(k * 0.17, 0.58, 0.02), 0.135, 0.105, glass, 7);
    k4.crack(hip, [V(k * 0.2, 0.95, 0.1), V(k * 0.22, 0.82, 0.12), V(k * 0.19, 0.7, 0.11)], 0.008, ember, 0.03);
    const shinB = bone(hip, V(k * 0.17, 0.56, 0.02), `shin${s}`);
    const lo = shinB.abs;
    const cop = k4.swell(lo, glassD, k * 0.17, 0.56, 0.08, 0.09, 0.085, 0.075, 7, 4);
    cop.rotation.x = -0.3;
    M(new THREE.SphereGeometry(0.022, 5, 4), ember, lo).position.set(k * 0.17, 0.56, 0.155);
    limb(lo, V(k * 0.17, 0.54, 0.02), V(k * 0.17, 0.13, 0.0), 0.105, 0.08, glass, 7);
    k4.crack(lo, [V(k * 0.17, 0.48, 0.1), V(k * 0.19, 0.36, 0.09), V(k * 0.16, 0.22, 0.085)], 0.008, ember, 0.03);
    const sab = M(new THREE.BoxGeometry(0.18, 0.1, 0.27), glassD, lo);
    sab.position.set(k * 0.17, 0.05, 0.05);
    const toe = M(new THREE.ConeGeometry(0.078, 0.15, 4), glassD, lo);
    toe.position.set(k * 0.17, 0.04, 0.23);
    toe.rotation.set(Math.PI / 2, Math.PI / 4, 0);
    toe.scale.set(1, 1, 0.55);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: a mail skirt under glass faulds, the belt, a rotted tabard's flaps ---------- */
  const bodyB = bone(g, V(0, 1.02, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.28, 0.34, 0.3, 9, 1, true), mail, body).position.set(0, 0.86, 0);
  const fauld = M(new THREE.CylinderGeometry(0.27, 0.31, 0.2, 9), glassD, body);
  fauld.position.set(0, 1.04, 0);
  const belt = M(new THREE.TorusGeometry(0.28, 0.025, 3, 12), iron, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 1.12, 0);
  belt.scale.set(1, 0.8, 1);
  const flapF = bone(body, V(0, 1.1, 0.22), 'flapF');
  k4.panel(flapF.abs, V(0, 1.11, 0.25), 0.34, 0.72, 0.02, 1, rag, 0.55);
  const flapB = bone(body, V(0, 1.1, -0.22), 'flapB');
  k4.panel(flapB.abs, V(0, 1.11, -0.25), 0.38, 0.82, 0.02, -1, rag, 0.5);

  /* ---------- chest: a glass breastplate split by a burning crack, the gorget, great pauldrons ---------- */
  const chestB = bone(body, V(0, 1.16, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.42, 0.0, 0.32, 0.31, 0.22, 9, 6);
  k4.swell(chest, glassD, 0, 1.22, 0.01, 0.28, 0.13, 0.2, 8, 4);
  // The crack the gate left: from the left shoulder down across the heart, embers inside.
  k4.crack(chest, [V(-0.2, 1.62, 0.17), V(-0.1, 1.52, 0.215), V(-0.02, 1.44, 0.225), V(0.06, 1.35, 0.22), V(0.03, 1.25, 0.21), V(0.12, 1.16, 0.19)], 0.014, ember, 0.06);
  k4.crack(chest, [V(-0.02, 1.44, 0.225), V(-0.12, 1.36, 0.21), V(-0.18, 1.27, 0.18)], 0.009, ember, 0.04);
  glowSprite(chest, '#ff7a52', 0.42, V(-0.02, 1.43, 0.27), 0.55);
  M(new THREE.CylinderGeometry(0.12, 0.155, 0.1, 8), glassD, chest).position.set(0, 1.66, 0);
  for (const k of [-1, 1]) {
    const pd = G(chest, k * 0.37, 1.64, 0);
    pd.rotation.z = -k * 0.35;
    const dome = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), glass, pd);
    dome.scale.set(0.22, 0.17, 0.22);
    for (let i = 1; i <= 2; i++) {
      const lame = M(new THREE.CylinderGeometry(0.22 - i * 0.012, 0.23 - i * 0.01, 0.06, 8, 1, true), i === 1 ? glassD : glass, pd);
      lame.position.y = -0.04 - i * 0.055;
    }
    // A shard standing off each pauldron.
    const sp = M(new THREE.ConeGeometry(0.04, 0.2, 4), glass, pd);
    sp.position.set(k * 0.05, 0.14, -0.02);
    sp.rotation.z = -k * 0.4;
  }

  /* ---------- the mantle of smoke: ragged dark panels down his back, frayed into wisps ---------- */
  const mantle = G(chest, 0, 1.66, -0.2);
  for (const [x, w, h, rz] of [[-0.18, 0.3, 1.25, 0.06], [0.18, 0.3, 1.2, -0.06], [0, 0.34, 1.35, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.02), w, h, 0.02, -1, smokeM, 0.6);
    pn.rotation.set(0.12, 0, rz);
  }

  /* ---------- head: a closed helm, a slit with two embers, a crown of the broken portcullis ---------- */
  const headB = bone(chest, V(0, 1.7, 0.01), 'head');
  const head = headB.abs;
  M(new THREE.CylinderGeometry(0.14, 0.15, 0.28, 9), glass, head).position.set(0, 1.84, 0.01);
  const dome = M(new THREE.SphereGeometry(0.142, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2), glass, head);
  dome.position.set(0, 1.98, 0.01);
  const ridge = M(new THREE.BoxGeometry(0.025, 0.26, 0.03), glassD, head);
  ridge.position.set(0, 1.86, 0.155);
  const slit = M(new THREE.BoxGeometry(0.23, 0.032, 0.03), char, head);
  slit.position.set(0, 1.88, 0.142);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.026, 6, 4), ember, head).position.set(k * 0.052, 1.88, 0.148);
    glowSprite(head, '#ff8a6e', 0.24, V(k * 0.052, 1.88, 0.18), 0.95);
  }
  k4.crack(head, [V(0.1, 2.0, 0.09), V(0.07, 1.93, 0.13), V(0.09, 1.84, 0.14)], 0.008, ember, 0.03);
  // The crown: bent iron bars of the portcullis, uneven, their tips glowing.
  const crown = G(head, 0, 2.0, 0.01);
  const ring = M(new THREE.TorusGeometry(0.15, 0.018, 4, 12), iron, crown);
  ring.rotation.x = Math.PI / 2;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.2;
    const h = 0.14 + ((i * 37) % 5) * 0.035;
    const bar = M(new THREE.BoxGeometry(0.022, h, 0.022), iron, crown);
    bar.position.set(Math.sin(a) * 0.15, h / 2, Math.cos(a) * 0.15);
    bar.rotation.set(Math.cos(a) * 0.25, 0, -Math.sin(a) * 0.25 + (i % 2 ? 0.15 : -0.1));
    const tip = M(new THREE.ConeGeometry(0.02, 0.05, 4), ember, crown);
    tip.position.set(Math.sin(a) * (0.15 + h * 0.24), h + 0.01, Math.cos(a) * (0.15 + h * 0.24));
  }
  const flames: THREE.Mesh[] = [];
  for (const [x, z, s, h] of [[0, 0, 1, 0.22], [0.05, -0.04, 0.7, 0.16], [-0.05, 0.03, 0.6, 0.14]] as const) {
    const f = M(new THREE.ConeGeometry(0.045 * s, h, 6), emberF, crown);
    f.position.set(x, 0.05 + h / 2, z);
    flames.push(f);
  }
  glowSprite(crown, '#ff8a6e', 0.55, V(0, 0.16, 0), 0.6);

  /* ---------- the shield arm: a slab of the gate, charred planks bound in iron, glowing in the seams ---------- */
  const armLB = bone(chest, V(-0.37, 1.58, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.38, 1.58, 0), V(-0.42, 1.24, 0.05), 0.09, 0.075, glass, 6);
  const foreLB = bone(aL, V(-0.42, 1.23, 0.06), 'foreL');
  const fL = foreLB.abs;
  k4.swell(fL, glassD, -0.42, 1.23, 0.06, 0.075, 0.075, 0.075, 6, 4);
  limb(fL, V(-0.42, 1.23, 0.06), V(-0.27, 1.18, 0.3), 0.072, 0.06, glass, 6);
  const shieldB = bone(fL, V(-0.25, 1.17, 0.34), 'shield');
  const sh = shieldB.abs;
  {
    const sg = G(sh, -0.12, 0.98, 0.42);
    sg.rotation.set(-0.06, 0.14, 0);
    const W = 0.66, H = 1.28, n = 5;
    // Planks side by side, each a little longer or shorter, splintered at the top.
    for (let i = 0; i < n; i++) {
      const x = -W / 2 + (W / n) * (i + 0.5);
      const h = H - ((i * 53) % 4) * 0.05;
      const pl = M(new THREE.BoxGeometry(W / n - 0.012, h, 0.06), char, sg);
      pl.position.set(x, (h - H) / 2, 0);
      const sp = M(new THREE.ConeGeometry(W / n / 2 - 0.01, 0.08, 3), char, sg);
      sp.position.set(x + 0.01, h - H / 2 + 0.03, 0);
      sp.scale.z = 0.4;
      // The seam beside it glows.
      if (i > 0) {
        const seam = M(new THREE.BoxGeometry(0.008, h * 0.8, 0.02), ember, sg);
        seam.position.set(x - W / n / 2, (h - H) / 2 - 0.04, 0.02);
      }
    }
    // Iron bands across, studded, and a hinge strap.
    for (const y of [-0.42, 0.05, 0.42]) {
      const band = M(new THREE.BoxGeometry(W + 0.03, 0.07, 0.03), iron, sg);
      band.position.set(0, y, 0.04);
      for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.017, 4, 3), iron, sg).position.set(-W / 2 + 0.07 + i * ((W - 0.14) / 4), y, 0.058);
    }
    const hinge = M(new THREE.CylinderGeometry(0.035, 0.035, 0.24, 6), iron, sg);
    hinge.position.set(W / 2 + 0.02, 0.05, 0.0);
    // A gash burnt through it, embers inside.
    k4.crack(sg, [V(-0.2, 0.32, 0.05), V(-0.1, 0.2, 0.06), V(-0.15, 0.06, 0.06), V(-0.04, -0.12, 0.06), V(-0.1, -0.3, 0.05)], 0.012, ember, 0.05);
    glowSprite(sg, '#ff7a52', 0.5, V(-0.08, 0.05, 0.12), 0.5);
  }
  M(new THREE.BoxGeometry(0.085, 0.095, 0.095), glassD, sh).position.set(-0.25, 1.17, 0.34);

  /* ---------- the sword arm: a long blade of black glass with a burning edge, point down ---------- */
  const armRB = bone(chest, V(0.37, 1.58, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.38, 1.58, 0), V(0.44, 1.23, 0.02), 0.09, 0.075, glass, 6);
  const foreRB = bone(aR, V(0.44, 1.22, 0.02), 'foreR');
  const fR = foreRB.abs;
  k4.swell(fR, glassD, 0.44, 1.22, 0.02, 0.075, 0.075, 0.075, 6, 4);
  limb(fR, V(0.44, 1.22, 0.02), V(0.46, 0.94, 0.1), 0.072, 0.062, glass, 6);
  const swordB = bone(fR, V(0.46, 0.9, 0.11), 'sword');
  const sw = swordB.abs;
  M(new THREE.BoxGeometry(0.095, 0.105, 0.105), glassD, sw).position.set(0.46, 0.9, 0.11);
  {
    const s = G(sw, 0.46, 0.9, 0.11);
    s.rotation.x = Math.PI - 0.35;
    M(new THREE.CylinderGeometry(0.02, 0.02, 0.22, 5), char, s).position.y = 0.0;
    M(new THREE.SphereGeometry(0.035, 5, 4), iron, s).position.y = -0.13;
    M(new THREE.BoxGeometry(0.32, 0.035, 0.05), iron, s).position.y = 0.12;
    M(new THREE.BoxGeometry(0.075, 0.95, 0.018), glass, s).position.y = 0.6;
    // The burning edge, both sides, and the point.
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.008, 0.9, 0.02), ember, s).position.set(k * 0.04, 0.6, 0);
    const pt = M(new THREE.ConeGeometry(0.04, 0.14, 4), glass, s);
    pt.position.y = 1.13;
    pt.scale.z = 0.3;
    glowSprite(s, '#ff7a52', 0.35, V(0, 0.75, 0.04), 0.4);
  }

  /* ---------- smoke breathing off him (the frames boil it) ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[0.32, -0.2, 0.7, 1.55], [-0.28, -0.24, 0.65, 1.5], [0.05, -0.32, 0.85, 1.3], [-0.12, -0.3, 0.6, 0.9]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.05, smokeM, rng, 0.06));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    flames.forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.5 * k, 0.85 + 0.3 * k);
    });
  };
  t(0);
  return done(c, CORVIN_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
